import type { ComponentNode, Page, SharedNode } from '@api/types.gen'
import type { NodeExtractor } from './schemaWalker'

type SharedNodeShape = {
  id: string
  title: string
  content: { children: unknown[] }
  events?: Record<string, unknown[]>
}

type SharedNodeResolver = (id: string) => SharedNode | SharedNodeShape | null

type LooseEventProcessor = { type?: unknown; data?: unknown } | null | undefined
type LooseEventBucket = Record<string, LooseEventProcessor[]> | undefined

function collectNavigateTargets(events: LooseEventBucket, out: Set<string>) {
  if (!events) return
  for (const eventName in events) {
    const procs = events[eventName]
    if (!Array.isArray(procs)) continue
    for (const proc of procs) {
      if (!proc) continue
      if (proc.type === 'navigate') {
        const data = proc.data as { pageId?: unknown } | undefined
        if (data && typeof data.pageId === 'string' && data.pageId.length > 0) {
          out.add(data.pageId)
        }
      }
    }
  }
}

function getSharedNodeRefId(node: ComponentNode): string | null {
  if (node.type !== 'SharedNodeRef') return null
  const id = node.fields?.sharedNodeId
  return typeof id === 'string' && id.length > 0 ? id : null
}

function walkNodeForLinks(
  node: ComponentNode,
  getSharedNodeById: SharedNodeResolver,
  out: Set<string>,
  visitedSharedNodeIds: Set<string>,
) {
  collectNavigateTargets(node.events as LooseEventBucket, out)

  const refId = getSharedNodeRefId(node)
  if (refId && !visitedSharedNodeIds.has(refId)) {
    const sn = getSharedNodeById(refId)
    if (sn) {
      visitedSharedNodeIds.add(refId)
      collectNavigateTargets(sn.events as LooseEventBucket, out)
      for (const child of (sn.content?.children ?? []) as ComponentNode[]) {
        walkNodeForLinks(child, getSharedNodeById, out, visitedSharedNodeIds)
      }
    }
  }

  for (const child of node.children ?? []) {
    walkNodeForLinks(child, getSharedNodeById, out, visitedSharedNodeIds)
  }
}

/**
 * Collect every internal `navigate` target reachable from a single page —
 * including page-level events, component events, and any SharedNodeRef
 * the page (or its referenced shared nodes) renders.
 *
 * Cycle-guarded by a per-call visited set keyed on shared-node id.
 */
export function getDirectLinkedPageIds(
  page: Page,
  getSharedNodeById: SharedNodeResolver,
): Set<string> {
  const out = new Set<string>()
  const visited = new Set<string>()
  collectNavigateTargets(page.events as LooseEventBucket, out)
  for (const child of page.content?.children ?? []) {
    walkNodeForLinks(child, getSharedNodeById, out, visited)
  }
  return out
}

/**
 * Run extractors over a page's component tree, expanding SharedNodeRefs into
 * their referenced shared-node templates. Mirrors how the runtime renders a
 * page (template + slot fillers), so e.g. an image inside a shared template
 * gets picked up when the template is referenced.
 *
 * Cycle-guarded by a single visited set across the whole call — a shared
 * node referenced multiple times is walked once, which is fine for image
 * extraction (`createImageExtractor` dedupes by id+preset internally).
 */
export function walkPageWithSharedNodes(
  page: Page,
  getSharedNodeById: SharedNodeResolver,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extractors: NodeExtractor<any>[],
) {
  const visitedSharedNodeIds = new Set<string>()

  function walkNode(node: ComponentNode) {
    extractors.forEach((e) => e.process(node))

    const refId = getSharedNodeRefId(node)
    if (refId && !visitedSharedNodeIds.has(refId)) {
      const sn = getSharedNodeById(refId)
      if (sn) {
        visitedSharedNodeIds.add(refId)
        for (const child of (sn.content?.children ?? []) as ComponentNode[]) {
          walkNode(child)
        }
      }
    }

    for (const child of node.children ?? []) {
      walkNode(child)
    }
  }

  for (const child of page.content?.children ?? []) {
    walkNode(child)
  }
}

/**
 * The page a node's `onClick` navigates to, when that is knowable at build time:
 * exactly one `navigate` processor, with no processor `rules`. Anything else —
 * no navigate, several, a rule-gated one — returns `null`.
 *
 * Used to give the clicked element a crawlable `<a href>` (PipeNavLink). Do NOT
 * swap in `getDirectLinkedPageIds`: that one also collects payment / timer
 * callbacks, which would advertise checkout → thank-you as a click link.
 */
export function getStaticClickTarget(events: LooseEventBucket): string | null {
  const procs = events?.onClick
  if (!Array.isArray(procs)) return null
  const navs = procs.filter((p) => p?.type === 'navigate')
  if (navs.length !== 1) return null
  const nav = navs[0] as { data?: { pageId?: unknown }; rules?: Record<string, unknown> }
  if (nav.rules && Object.keys(nav.rules).length > 0) return null
  const pageId = nav.data?.pageId
  return typeof pageId === 'string' && pageId.length > 0 ? pageId : null
}
